import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useSupplierActions } from "@/app/Suppliers/Hooks/useSupplierActions";
import {
  SupplierListItem,
  supplierListQuery,
} from "@/app/Suppliers/GraphQL/supplierListQuery";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { VariablesOf } from "@graphql-typed-document-node/core";
import {
  QuerySuppliersOrderByColumn,
  QuerySuppliersOrderByOrderByClause,
  SortOrder,
} from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { Highlighter } from "@/lib/Components/Highlighter";
import { useState } from "react";
import { SelectFilter } from "@/lib/Components/DataTable/Filters/SelectFilter";
import { Badge } from "@/components/catalyst/badge";

type SupplierTableColumnId =
  | "supplier"
  | "email"
  | "phone"
  | "measurement"
  | "hire_unit_type"
  | "created_at";

export function SupplierTable({
  id,
  queryVariables,
  title,
}: {
  id: TableId;
  queryVariables?: Partial<VariablesOf<typeof supplierListQuery>>;
  title?: string;
}) {
  "use no memo";

  const getActions = useSupplierActions();
  const tCommon = useTranslations("common");
  const tRelocation = useTranslations("relocation");
  const t = useTranslations("supplier.table");

  const [showArchived, setShowArchived] = useState(false);

  const columns: DataTableColDef<
    SupplierListItem,
    any,
    SupplierTableColumnId
  >[] = [
    {
      id: "supplier",
      accessorFn: (s) => s,
      cell: ({ row, table }) => {
        const search = table.options.meta?.search ?? "";
        return (
          <div className="flex items-center space-x-3">
            <SupplierIcon
              model={row.original}
              className="h-8 w-8 rounded-md border border-gray-200 object-contain"
            />
            <ModelLinkCell
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/suppliers/$id/bookings"
              params={(prev) => ({ ...prev, id: row.original.id })}
              viewTransition
            >
              <Highlighter searchString={search}>
                {row.original.name}
              </Highlighter>
            </ModelLinkCell>
            {row.original.archived_at ? (
              <Badge color="zinc">Archived</Badge>
            ) : null}
          </div>
        );
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("supplier")} />
      ),
    },
    {
      id: "email",
      accessorFn: (row) => row.email ?? "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("email")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "phone",
      accessorFn: (row) => row.phone ?? "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("phone")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "measurement",
      accessorFn: (row) =>
        row.measurement ? tCommon(`measurement.${row.measurement}`) : "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("measurement")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "hire_unit_type",
      accessorFn: (row) =>
        row.hire_unit_type
          ? tRelocation(`hire_unit_type_capitalized.${row.hire_unit_type}`)
          : "--",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("hireUnitType")} />
      ),
      cell: DeEmphasisedCell,
    },
    {
      id: "created_at",
      accessorFn: (row) => row.created_at,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("createdAt")} />
      ),
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <DataTable
      id={id}
      initialSorting={[{ id: "supplier", desc: false }]}
      title={title}
      getActions={getActions}
      columns={columns}
      filters={(table) => (
        <SelectFilter
          label="Status"
          options={[
            { label: "Active", value: "active" },
            { label: "Archived", value: "archived" },
          ]}
          selected={showArchived ? "archived" : "active"}
          onChange={(value) => {
            setShowArchived(value === "archived");
            table.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        />
      )}
      getQueryVariables={({ search, pagination, sorting }) => {
        return {
          search: showArchived ? undefined : search,
          archived: showArchived,
          page: pagination.pageIndex,
          first: pagination.pageSize,
          orderBy:
            sorting.length && !search
              ? sorting.map((s) => {
                  const order = s.desc ? SortOrder.Desc : SortOrder.Asc;

                  let column: QuerySuppliersOrderByColumn;

                  switch (s.id) {
                    case "supplier":
                      column = QuerySuppliersOrderByColumn.Name;
                      break;
                    case "created_at":
                      column = QuerySuppliersOrderByColumn.CreatedAt;
                      break;
                    default:
                      column = QuerySuppliersOrderByColumn.Name;
                      break;
                  }

                  return {
                    order,
                    column,
                  } satisfies QuerySuppliersOrderByOrderByClause;
                })
              : undefined,
          ...queryVariables,
        };
      }}
      document={supplierListQuery}
      accessor={(res) => res.suppliers}
    />
  );
}
